import React, { useContext, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, withRouter, useHistory } from 'react-router-dom'

import { getAllEventsOpening } from '_redux/modules/event'
import { getMyOrders } from '_redux/modules/order'
import { getMyGroups } from '_redux/modules/group'

import { DOMAIN, EVENT_STATUS_OPEN, DESKTOP, PHONE } from '_utils/constant'
import { convertToCurrentGMT, isDisableEvent } from '_utils/function'
import { getNavTabsQty, getUserInfo, setNavTabsQty, setDeviceUser } from '_utils/localData'
import { DeviceUserContext } from '_context/DeviceUserContext'

import './style.scss'

const countOpenEvents = (allEvents) =>
  allEvents?.filter((item) => item.status === EVENT_STATUS_OPEN)?.length || 0

const countUnclosedPurchases = (myOrders) =>
  myOrders.filter((item) => {
    const isDisable = isDisableEvent(
      item?.status,
      item?.buyerStatus,
      convertToCurrentGMT(item?.ePickupTime),
      item?.ePickupDuration
    )
    return !isDisable
  }).length

const Header = ({ location, history: propsHistory }) => {
  const history = useHistory() || propsHistory
  const dispatch = useDispatch()
  const [pathname, setPathname] = useState(location.pathname.split('/'))
  const { allEvents } = useSelector((state) => state.event)
  const { myOrders } = useSelector((state) => state.order)
  const { myGroups } = useSelector((state) => state.group)
  const { checkDeviceUser, setCheckDeviceUser } = useContext(DeviceUserContext) || {}
  const [isDesktopWidth, setIsDesktopWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 1203 : false
  )

  useEffect(() => {
    const handleResize = () => {
      setIsDesktopWidth(window.innerWidth >= 1203)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const userInfo = getUserInfo()

  const { domainFeatures = [], groups } = userInfo

  const currDomainFeature = domainFeatures.find((item) => item.domainName === DOMAIN) || {}

  const group = groups?.length && groups?.find((g) => currDomainFeature.groupId === g.gid)

  // Show the last known qty instantly (from localStorage) while fresh data loads in the background
  const cachedQty = useRef(getNavTabsQty())
  const [totalEvents, setTotalEvents] = useState(cachedQty.current?.totalEvents || 0)
  const [totalMyPurchases, setTotalMyPurchases] = useState(cachedQty.current?.totalMyPurchases || 0)
  const [totalMyGroups, setTotalMyGroups] = useState(cachedQty.current?.totalMyGroups || 0)

  // Redux slices reset to their initial value on every page load, so the first effect run
  // after mount doesn't represent real data yet - skip it and only react to actual updates.
  const hasSkippedInitialRun = useRef({ events: false, orders: false, groups: false })

  useEffect(() => {
    if (!getNavTabsQty()) {
      dispatch(getAllEventsOpening)
      dispatch(getMyOrders)
      dispatch(getMyGroups)
    }
  }, [dispatch])

  useEffect(() => {
    if (!hasSkippedInitialRun.current.events) {
      hasSkippedInitialRun.current.events = true
      return
    }
    const total = countOpenEvents(allEvents)
    setTotalEvents(total)
    setNavTabsQty({ totalEvents: total })
  }, [allEvents])

  useEffect(() => {
    if (!hasSkippedInitialRun.current.orders) {
      hasSkippedInitialRun.current.orders = true
      return
    }
    const total = countUnclosedPurchases(myOrders)
    setTotalMyPurchases(total)
    setNavTabsQty({ totalMyPurchases: total })
  }, [myOrders])

  useEffect(() => {
    if (!hasSkippedInitialRun.current.groups) {
      hasSkippedInitialRun.current.groups = true
      return
    }
    if (myGroups === null) return
    const total = myGroups.length
    setTotalMyGroups(total)
    setNavTabsQty({ totalMyGroups: total })
  }, [myGroups])

  useEffect(() => {
    setPathname(location.pathname.split('/'))
  }, [location])

  const handleSwitchToDesktop = () => {
    setDeviceUser(DESKTOP)
    setCheckDeviceUser?.(DESKTOP)
    history?.push('/manage-orders')
  }

  const isPremium = Boolean(userInfo?.isUsingFeature)
  const isMobileView = checkDeviceUser === PHONE || checkDeviceUser !== DESKTOP
  const showSwitchViewBtn = isPremium && isDesktopWidth && isMobileView

  return (
    <div className='header align-self-start'>
      <div className='container-fluid d-flex align-items-center justify-content-between position-relative px-3' style={{ height: '52px' }}>
        {showSwitchViewBtn && (
          <div
            onClick={handleSwitchToDesktop}
            className='toggle-drawer-btn'
            title='Switch to desktop view'
          >
            <svg
              width='16'
              height='16'
              viewBox='0 0 24 24'
              fill='none'
              stroke='white'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <line x1='3' y1='6' x2='21' y2='6' />
              <line x1='3' y1='12' x2='21' y2='12' />
              <line x1='3' y1='18' x2='21' y2='18' />
            </svg>
          </div>
        )}

        {userInfo?.groups?.length === 1 && !group ? (
          <div className='topbar-brand text-center'>
            <div className='topbar-logo'>{userInfo?.groups?.[0]?.name}</div>
            <div className='topbar-tagline'>Good things must share!</div>
          </div>
        ) : group ? (
          <div className='topbar-brand text-center'>
            <Link to='/home' style={{ textDecoration: 'none' }}>
              <div className='topbar-logo'>{group?.name}</div>
              <div className='topbar-tagline'>Good things must share!</div>
            </Link>
          </div>
        ) : (
          <div className='topbar-brand text-center'>
            <Link to='/home' style={{ textDecoration: 'none' }}>
              <div className='topbar-logo'>
                co<span className='dot'>•</span>bee
              </div>
              <div className='topbar-tagline'>Good things must share!</div>
            </Link>
          </div>
        )}

        <div className='topbar-right d-flex align-items-center ms-auto' style={{ gap: '12px' }}>
          <Link to='/user/profile' className='topbar-notif' style={{ color: 'white' }}>
            <svg
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='white'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9' />
              <path d='M13.73 21a2 2 0 0 1-3.46 0' />
            </svg>
            <span className='topbar-notif-dot' />
          </Link>
          <Link to='/user/profile' className='avatar-top__img'>
            <img
              id='userAvatar'
              src={
                userInfo?.photoUrl
                  ? userInfo.photoUrl
                  : process.env.REACT_APP_AVATAR_URL + userInfo?.name
              }
              alt=''
            />
          </Link>
        </div>
      </div>
      <div className='nav-menu'>
        <div className='container'>
          <div className='row'>
            <div className='col-4'>
              <Link to='/home' className={pathname[1] === 'home' ? 'active' : ''}>
                Events {!!totalEvents && <span id='totalEvents'>({totalEvents})</span>}
              </Link>
            </div>
            <div className='col-4'>
              <Link
                to='/event/list-my-purchases'
                className={
                  pathname[1] === 'event' && pathname[2] === 'list-my-purchases' ? 'active' : ''
                }
              >
                My Purchase{' '}
                {!!totalMyPurchases && <span id='totalMyPurchases'>({totalMyPurchases})</span>}
              </Link>
            </div>
            <div className='col-4'>
              {Object.keys(currDomainFeature).length ? (
                <Link
                  to={`/group/list-events?groupId=${currDomainFeature?.groupId}`}
                  className={pathname[1] === 'group' ? 'active' : ''}
                >
                  My Group {!!totalMyGroups && <span id='totalMyGroups'>({totalMyGroups})</span>}
                </Link>
              ) : (
                <Link
                  to='/group/list-my-groups'
                  className={pathname[1] === 'group' ? 'active' : ''}
                >
                  My Group {!!totalMyGroups && <span id='totalMyGroups'>({totalMyGroups})</span>}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default withRouter(Header)
